import React from 'react';
import { Redirect, Route, RouteProps, useRouteMatch } from 'react-router-dom';
import { AuthStates, useAuthState } from '@vis-auth/react';
import { AccountType } from 'eve-account-client';
import { EverifyUserRole } from '@vis-auth/vis-user-client';
import { PageLoader } from '@ver-uds/react';
import MainRoute from './MainRoute/MainRoute';
import { InternalRoutes } from '../constants';

export interface PrivateRouteProps extends RouteProps {
  roles?: EverifyUserRole[];
  accountTypes?: AccountType[];
  path: string | string[];
  exact?: boolean;
  isOTCAuth?: boolean;
}
const PrivateRoute = ({
  roles,
  accountTypes,
  path,
  exact = false,
  children,
  isOTCAuth = false,
}: PrivateRouteProps): JSX.Element => {
  const { user, state } = useAuthState();
  // eslint-disable-next-line camelcase
  let accountId = user?.account_id as string;
  const routeMatch = useRouteMatch<{ subAccountId?: string }>(path);
  if (routeMatch?.params.subAccountId) {
    accountId = routeMatch.params.subAccountId;
  }
  const noMatchingRole = roles && roles?.every((role) => !user?.roles.includes(role));
  const isValidOTCAuth = isOTCAuth && !user && state === AuthStates.authenticated;

  // Audience checks will fail until state is authenticated
  if (isOTCAuth && !user && state === AuthStates.authenticating) {
    return <PageLoader label="Loading" sectionOverlay />;
  }

  // Prevent enrollment auth from using account query
  if (user?.roles?.includes(EverifyUserRole.ENROLLMENT) || isValidOTCAuth) {
    return (
      <Route
        path={path}
        exact={exact}
        render={() => {
          // If route is for enrollment, show it
          if (!noMatchingRole || isValidOTCAuth) {
            return children;
          }
          // Otherwise, redirect to page not found
          return <Redirect to={InternalRoutes.PAGE_NOT_FOUND} />;
        }}
      />
    );
  }

  // Prevent accessing OTC gated pages
  if (isOTCAuth && user) {
    return <Redirect to={InternalRoutes.PAGE_NOT_FOUND} />;
  }

  return (
    <MainRoute
      path={path}
      exact={exact}
      noMatchingRole={noMatchingRole}
      accountIdForQuery={accountTypes && accountId}
      accountTypes={accountTypes}
    >
      {children}
    </MainRoute>
  );
};
export default PrivateRoute;
